import { useEffect, useRef } from 'react';
import type { PlaybackAdapter, PlayerControls } from '@mlm/ui';
import { nextRate, resolvePlayerKeyAction } from '@mlm/shared';
import { usePlayerPrefs } from '../lib/player/prefs-store';

/**
 * Player keyboard shortcuts.
 *
 * The listener is window-level because the learner's focus is usually nowhere
 * near the video — but that is exactly why every "not ours" rule in
 * `resolvePlayerKeyAction` matters: this page also hosts a Q&A textarea, a
 * notes editor, a sidebar of lesson buttons and Mark complete. It is not
 * mounted at all while a test pane is open (a learner mid-essay must never have
 * a keystroke stolen) or inside the storefront preview dialog.
 */
export function usePlayerKeyboard({
  enabled,
  adapter,
  controls,
  kind,
  onBookmark,
}: {
  enabled: boolean;
  adapter: PlaybackAdapter | null;
  controls: PlayerControls | null;
  kind: 'video' | 'iframe';
  onBookmark?: () => void;
}) {
  const ref = useRef({ adapter, controls, kind, onBookmark });
  ref.current = { adapter, controls, kind, onBookmark };

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const {
        adapter: player,
        controls: handles,
        kind: backend,
        onBookmark: bookmark,
      } = ref.current;
      if (!player) return;
      const target = event.target as HTMLElement | null;
      const action = resolvePlayerKeyAction({
        key: event.key,
        shiftKey: event.shiftKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        altKey: event.altKey,
        repeat: event.repeat,
        targetTag: target?.tagName,
        targetRole: target?.closest('[role]')?.getAttribute('role') ?? null,
        targetEditable: target?.isContentEditable === true,
        insidePlayer: !!target?.closest('[data-player-root]'),
        // Radix marks the open dialog; the free-preview player lives in one.
        dialogOpen: !!document.querySelector('[role="dialog"][data-state="open"]'),
        kind: backend,
      });
      if (!action) return;
      event.preventDefault();

      const snapshot = player.getSnapshot();
      switch (action.type) {
        case 'toggle':
          if (snapshot.state === 'playing') player.pause();
          else void player.play();
          break;
        case 'seekBy':
          player.seek(Math.max(0, snapshot.currentTime + action.seconds));
          break;
        case 'seekPct':
          if (snapshot.duration) player.seek((snapshot.duration * action.pct) / 100);
          break;
        case 'volumeBy': {
          const volume = Math.min(1, Math.max(0, snapshot.volume + action.delta));
          player.setMuted(false);
          player.setVolume(volume);
          usePlayerPrefs.getState().setVolume(volume);
          break;
        }
        case 'mute':
          player.setMuted(!snapshot.muted);
          usePlayerPrefs.getState().setMuted(!snapshot.muted);
          break;
        case 'fullscreen':
          handles?.toggleFullscreen();
          break;
        case 'captions':
          handles?.toggleCaptions();
          break;
        case 'rateStep': {
          const rate = nextRate(snapshot.rate, action.dir, snapshot.rates);
          void player.setRate(rate).then((ok) => {
            if (!ok) return;
            usePlayerPrefs.getState().setRate(rate);
            handles?.flash(rate === 1 ? 'Normal speed' : `${rate}× speed`);
          });
          break;
        }
        case 'bookmark':
          bookmark?.();
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
